'use client';

import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useSwipeable } from 'react-swipeable';
import { Button } from '@/components/ui/button'
import PublicInventoryBrowserModal from './PublicInventoryBrowserModal'
import TerpeneBrowserModal from '@/components/terpenes/TerpeneBrowserModal'
import { ContactUsModal } from './ContactUsModal'
import { GoldStandardSlide, LabResultsSlide } from './terminal/TerminalSlides'
import { useTerpeneStore } from '@/stores/terpeneStore'
import { useReducedMotion } from '@/lib/hooks/useReducedMotion'

const SLIDE_DURATION = 5000 // 5 seconds

export function HolographicTerminal() {
  const [currentSlide, setCurrentSlide] = useState(0)
  const [isPaused, setIsPaused] = useState(false)
  const [inventoryModalOpen, setInventoryModalOpen] = useState(false)
  const [typedText, setTypedText] = useState('');
  const [showTyping, setShowTyping] = useState(false);
  const [typingComplete, setTypingComplete] = useState(false);
  const { isModalOpen: terpeneModalOpen, openModal: openTerpeneModal, closeModal: closeTerpeneModal } = useTerpeneStore()
  const intervalRef = useRef<NodeJS.Timeout | undefined>(undefined)
  const fullText = 'Quality your customers will see and feel.';
  const reducedMotion = useReducedMotion()
  const TRANSITION_DURATION = reducedMotion ? 0 : 0.5
  
  const slides = [
    <LabResultsSlide key="lab" onOpenTerpeneModal={openTerpeneModal} />,
    <GoldStandardSlide key="gold" />
  ]

  // Typing animation effect
  useEffect(() => {
    const timeouts: NodeJS.Timeout[] = [];

    // Start typing animation after a brief pause
    const startTimer = setTimeout(() => {
      setShowTyping(true);
      let currentText = '';
      for (let i = 0; i < fullText.length; i++) {
        const typingTimer = setTimeout(() => {
          currentText += fullText[i];
          setTypedText(currentText);
          // Mark typing as complete when we reach the end
          if (currentText.length === fullText.length) {
            const completeTimer = setTimeout(() => setTypingComplete(true), 300);
            timeouts.push(completeTimer);
          }
        }, i * 50);
        timeouts.push(typingTimer);
      }
    }, 800);
    timeouts.push(startTimer);

    // ✅ Cleanup ALL timeouts to prevent memory leak
    return () => {
      timeouts.forEach(clearTimeout);
    };
  }, []);

  // Auto-advance logic
  useEffect(() => {
    if (!isPaused) {
      intervalRef.current = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % slides.length)
      }, SLIDE_DURATION)
    }
    
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [isPaused, slides.length])

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        navigateSlide('prev')
      } else if (e.key === 'ArrowRight') {
        navigateSlide('next')
      }
    }
    
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  const navigateSlide = (direction: 'prev' | 'next') => {
    setIsPaused(true)
    setTimeout(() => setIsPaused(false), 10000) // Resume after 10s
    
    if (direction === 'next') {
      setCurrentSlide((prev) => (prev + 1) % slides.length)
    } else {
      setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length)
    }
  }

  // Touch/swipe handlers
  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => navigateSlide('next'),
    onSwipedRight: () => navigateSlide('prev'),
    trackMouse: false
  })

  return (
    <div
      className="relative w-full flex flex-col"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      {...swipeHandlers}
    >
      {/* Terminal Header - simplified layout */}
      <div className="backdrop-blur-xl bg-white/[0.02] border border-white/10 rounded-t-2xl px-4 py-2">
        <div className="flex items-center justify-between">
          {/* Left: View Inventory button */}
          <Button
            onClick={() => setInventoryModalOpen(true)}
            variant="outline"
            className="rounded-full border-yellow-400 text-yellow-300 hover:bg-yellow-500/10 hover:border-yellow-300 shadow-[0_0_18px_rgba(234,179,8,0.35)] pill-border-glow-gold text-xs sm:text-sm"
            size="sm"
          >
            <span className="hidden sm:inline">View Inventory / Request Samples</span>
            <span className="sm:hidden">View / Request</span>
          </Button>

          {/* Right: Navigation with arrows + dots */}
          <div className="flex items-center gap-3">
            {/* Left Arrow */}
            <button
              onClick={() => navigateSlide('prev')}
              className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
              aria-label="Previous slide"
            >
              <ChevronLeft className="h-5 w-5 text-white/60 hover:text-white" />
            </button>

            {/* Dots Navigation */}
            <div className="flex gap-2">
              {slides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setCurrentSlide(index)
                    setIsPaused(true)
                    setTimeout(() => setIsPaused(false), 10000)
                  }}
                  className={`transition-all duration-200 ${
                    currentSlide === index
                      ? 'w-3 h-3 rounded-full bg-yellow-400 shadow-[0_0_10px_rgba(250,204,21,0.6)]'
                      : 'w-2 h-2 rounded-full bg-white/30 hover:bg-white/50'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>

            {/* Right Arrow */}
            <button
              onClick={() => navigateSlide('next')}
              className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
              aria-label="Next slide"
            >
              <ChevronRight className="h-5 w-5 text-white/60 hover:text-white" />
            </button>
          </div>
        </div>
      </div>
      
      {/* Terminal Body - FIXED height to prevent ANY layout shift between slides */}
      <motion.div
        className="backdrop-blur-xl bg-black/40 border-x border-b border-white/10 rounded-b-2xl shadow-elev-1 p-4 overflow-hidden relative h-[660px] md:h-[680px]"
      >
        {/* Subtle overlay pattern */}
        <div className="pointer-events-none absolute inset-0 opacity-20"
             style={{
               backgroundImage: 'radial-gradient(circle at 25% 25%, rgba(255,255,255,0.02) 0%, transparent 50%)'
             }}></div>

        {/* Refined corner accents */}
        <div className="pointer-events-none absolute top-2 left-2 w-3 h-3 border-t border-l border-white/20 rounded-tl"></div>
        <div className="pointer-events-none absolute top-2 right-2 w-3 h-3 border-t border-r border-white/20 rounded-tr"></div>
        <div className="pointer-events-none absolute bottom-2 left-2 w-3 h-3 border-b border-l border-white/20 rounded-bl"></div>
        <div className="pointer-events-none absolute bottom-2 right-2 w-3 h-3 border-b border-r border-white/20 rounded-br"></div>

        <div className="h-full flex flex-col">
          {/* Typing animation text */}
          {showTyping && (
            <div className="font-mono text-sm md:text-base mb-3">
              <div className="text-white/80">
                <span className="bg-gradient-to-r from-eskerium-blue to-eskerium-gold bg-clip-text text-transparent">
                  {typedText}
                </span>
                <span className="animate-pulse">_</span>
              </div>
            </div>
          )}

          {/* ✅ OPTIMIZED: FIXED height container - prevents layout shift between slides */}
          <div className="relative flex-1">
            <AnimatePresence mode="wait">
              {typingComplete && (
                <motion.div
                  key={currentSlide}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{
                    duration: TRANSITION_DURATION,
                    ease: 'easeInOut'
                  }}
                  className="absolute inset-0 overflow-y-auto"
                  style={{ willChange: reducedMotion ? 'auto' : 'opacity' }}
                >
                  {slides[currentSlide]}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

        </div>
      </motion.div>

      {/* Auto-advance indicator */}
      {!isPaused && (
        <motion.div
          className="absolute bottom-4 left-1/2 transform -translate-x-1/2"
          initial={{ width: '0%' }}
          animate={{ width: '100%' }}
          transition={{ duration: SLIDE_DURATION / 1000, ease: 'linear' }}
          style={{
            height: '2px',
            background: 'linear-gradient(90deg, transparent, #3B82F6, transparent)',
            width: '200px'
          }}
        />
      )}

      {/* Public Inventory Browser Modal */}
      <PublicInventoryBrowserModal 
        open={inventoryModalOpen} 
        onOpenChange={setInventoryModalOpen} 
      />

      {/* Terpene Browser Modal */}
      <TerpeneBrowserModal
        open={terpeneModalOpen}
        onOpenChange={(open) => open ? openTerpeneModal() : closeTerpeneModal()}
      />
    </div>
  );
}